import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, Star } from "lucide-react";
import { Button } from "@shared/components/ui/button";
import { format, startOfMonth, endOfMonth, isBefore, startOfDay, addMonths } from "date-fns";
import { cn } from "@shared/utils/cn";
import type { MergedCalendarDay, AvailabilityDisplayType } from "../types/calendar";
import { isDisplayTypeClickable } from "../utils/availability";

interface CalendarProps {
  value?: Date;
  onChange: (date: Date) => void;
  availability?: MergedCalendarDay[];
  isLoading?: boolean;
  locale?: Locale;
  doubleMonth?: boolean;
}

function getDayStyles(
  isSelected: boolean,
  isToday: boolean,
  displayType?: AvailabilityDisplayType,
  hasAvailabilityData?: boolean,
): string {
  if (isSelected) {
    return "bg-brand text-brand-foreground shadow-md font-semibold";
  }

  // Day outside availability range when data is loaded → disabled
  if (hasAvailabilityData && !displayType) {
    return cn(
      "text-foreground/20 cursor-not-allowed",
      isToday ? "ring-2 ring-brand/30 ring-offset-1" : "",
    );
  }

  const todayDefault = isToday
    ? "border border-brand/30 bg-brand/5 text-brand font-semibold hover:bg-brand/10"
    : "text-foreground/70 hover:bg-foreground/5";
  const todayRing = isToday ? "ring-2 ring-brand/30 ring-offset-1" : "";

  // No availability data loaded yet → neutral
  if (!displayType) return todayDefault;

  // All clickable open states (available / request / waiting-list / walk-in)
  // share the same neutral cell style — every day reads as a clean number.
  // The small colored dot under the cell (see StateDot below) carries the
  // state distinction without crowding the number or turning the grid into
  // a sea of competing coloured shells.
  switch (displayType) {
    case 'not-available':
      return cn("text-foreground/25 cursor-not-allowed", todayRing);
    case 'closed':
      return cn("text-foreground/15 cursor-not-allowed", todayRing);
    case 'available':
    case 'request':
    case 'waiting-list':
    case 'walk-in':
    default:
      return todayDefault;
  }
}

/**
 * Small colored dot placed INSIDE the cell at the bottom (in the empty space
 * below the number) that communicates exceptional availability states.
 * Positioned inside the cell so it doesn't disturb the row rhythm — the
 * calendar's vertical spacing stays identical to a dot-less view.
 * Available days have no dot (the default state needs no marker). Closed/
 * not-available also have no dot (the muted cell colour does the work).
 * For colorblind users the legend + aria-label carry the state information.
 */
function StateDot({ displayType, isSelected }: { displayType?: AvailabilityDisplayType; isSelected?: boolean }) {
  if (isSelected || !displayType) return null;

  const colorClass: Partial<Record<AvailabilityDisplayType, string>> = {
    'waiting-list': 'bg-amber-500',
    'walk-in': 'bg-blue-500',
  };
  const cls = colorClass[displayType];
  if (!cls) return null;

  return (
    <span
      // bottom-1 (4px) rather than bottom-1.5 (6px) so the dot sits a hair
      // closer to the cell's bottom edge and a hair further from the
      // centered number. Reads as a state marker, not a typographic
      // descender hanging off the digit.
      className={cn('absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full', cls)}
      aria-hidden="true"
    />
  );
}

/**
 * Get availability status text for aria-label.
 */
function getAvailabilityStatusText(
  displayType: AvailabilityDisplayType | undefined,
  t: (key: string) => string,
): string {
  if (!displayType) return '';

  switch (displayType) {
    case 'available':
    case 'request':
      return `, ${t('CALENDAR_LEGEND_AVAILABLE')}`;
    case 'waiting-list':
      return `, ${t('CALENDAR_LEGEND_WAITING_LIST')}`;
    case 'walk-in':
      return `, ${t('CALENDAR_LEGEND_WALK_IN')}`;
    case 'not-available':
      return `, ${t('STRING_NOT_AVAILABLE')}`;
    case 'closed':
      return `, ${t('ROLLOVER_CALENDAR_DAY_CLOSED')}`;
    default:
      return '';
  }
}

function MonthGrid({
  month,
  value,
  today,
  availabilityMap,
  hasAvailabilityData,
  onChange,
  locale,
  showTitle = true,
}: {
  month: Date;
  value?: Date;
  today: Date;
  availabilityMap: Map<string, MergedCalendarDay>;
  hasAvailabilityData: boolean;
  onChange: (date: Date) => void;
  locale?: Locale;
  showTitle?: boolean;
}) {
  const { t } = useTranslation('booking');

  const days = useMemo(() => {
    const lastDay = endOfMonth(month);
    const result: Date[] = [];
    for (let d = 1; d <= lastDay.getDate(); d++) {
      result.push(new Date(month.getFullYear(), month.getMonth(), d));
    }
    return result;
  }, [month]);

  // Generate localized weekday abbreviations starting Monday (Mon=0 .. Sun=6).
  // Week starts on Monday so the weekend stays grouped at the right edge.
  const weekDays = useMemo(() => {
    const base = new Date(2024, 0, 1); // Known Monday
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(base);
      d.setDate(base.getDate() + i);
      return format(d, 'EEEEEE', { locale });
    });
  }, [locale]);

  const isSelected = (date: Date) =>
    value &&
    date.getDate() === value.getDate() &&
    date.getMonth() === value.getMonth() &&
    date.getFullYear() === value.getFullYear();

  const isDateToday = (date: Date) =>
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear();

  const isPast = (date: Date) => isBefore(startOfDay(date), today);
  const getAvailability = (date: Date) => availabilityMap.get(format(date, 'yyyy-MM-dd'));
  const firstDayOfMonth = startOfMonth(month);

  return (
    <div>
      {showTitle && (
        <h4 className="text-center text-base font-display text-brand mb-3">
          {format(month, "MMMM yyyy", { locale })}
        </h4>
      )}

      {/* Weekday headers */}
      <div className="grid grid-cols-7">
        {weekDays.map(day => (
          <div key={day} className="text-center text-sm font-semibold text-foreground/60 tracking-wider py-2">
            {day}
          </div>
        ))}
      </div>

      {/* Day grid */}
      <div className="grid grid-cols-7" role="grid">
        {/* Convert Date#getDay() (Sun=0..Sat=6) to a Monday-first column offset */}
        {Array((firstDayOfMonth.getDay() + 6) % 7).fill(null).map((_, index) => (
          <div key={`empty-${index}`} className="aspect-square" aria-hidden="true" />
        ))}
        {days.map((date) => {
          const dayAvailability = getAvailability(date);
          const dateSelected = isSelected(date);
          const isDatePast = isPast(date);
          const isClickable = !isDatePast && (
            hasAvailabilityData
              ? !!dayAvailability && isDisplayTypeClickable(dayAvailability.displayType)
              : !dayAvailability || isDisplayTypeClickable(dayAvailability.displayType)
          );
          const showStar = dayAvailability?.isSpecialDay && dayAvailability?.highlightSpecialDay && !dateSelected;

          const dayStyles = isDatePast
            ? "text-foreground/20 cursor-not-allowed"
            : getDayStyles(dateSelected, isDateToday(date), dayAvailability?.displayType, hasAvailabilityData);

          const statusText = hasAvailabilityData
            ? getAvailabilityStatusText(dayAvailability?.displayType, t)
            : '';

          return (
            <div key={date.toString()} className="flex items-center justify-center p-1">
              <button
                onClick={() => isClickable && onChange(date)}
                disabled={!isClickable}
                aria-selected={dateSelected}
                aria-label={`${format(date, "MMMM d, yyyy", { locale })}${statusText}`}
                role="gridcell"
                className={cn(
                  "w-full aspect-square max-w-[44px] rounded-full flex items-center justify-center text-base font-medium relative transition-colors",
                  dayStyles,
                )}
              >
                {showStar && (
                  <Star
                    size={10}
                    className="absolute -top-0.5 -right-0.5 text-yellow-500 fill-yellow-400"
                  />
                )}
                {date.getDate()}
                <StateDot displayType={dayAvailability?.displayType} isSelected={!!dateSelected} />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}


const Calendar: React.FC<CalendarProps> = ({
  value,
  onChange,
  availability = [],
  isLoading = false,
  locale,
  doubleMonth = false,
}) => {
  const { t } = useTranslation('booking');
  const today = useMemo(() => startOfDay(new Date()), []);
  const [currentMonth, setCurrentMonth] = useState(() => value ? startOfMonth(value) : startOfMonth(today));

  const availabilityMap = useMemo(() => {
    const map = new Map<string, MergedCalendarDay>();
    for (const day of availability) {
      map.set(day.date, day);
    }
    return map;
  }, [availability]);

  const hasAvailabilityData = availability.length > 0;

  const canGoToPreviousMonth = useMemo(() => {
    const previousMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
    return !isBefore(endOfMonth(previousMonth), today);
  }, [currentMonth, today]);

  const handlePreviousMonth = () => {
    if (canGoToPreviousMonth) {
      setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
    }
  };

  const handleNextMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const nextMonth = useMemo(() => addMonths(currentMonth, 1), [currentMonth]);

  return (
    <div className={cn(
      // On mobile, drop the top/side card chrome — only a bottom border with
      // rounded corners closes it off, so the grid flows full-width into the
      // page while still feeling deliberately ended. Full card on sm+.
      "relative bg-card border-b border-border rounded-b-2xl sm:border sm:rounded-xl sm:shadow-sm",
      doubleMonth ? "px-4 pt-4 pb-6 sm:p-6" : "px-4 pt-4 pb-6 sm:p-4",
    )}>
      {/* Navigation arrows */}
      <div className="flex items-center justify-between mb-2" role="navigation" aria-label={t('A11Y_CALENDAR_NAVIGATION')}>
        <Button
          onClick={handlePreviousMonth}
          variant="ghost"
          size="icon"
          aria-label={t('A11Y_PREVIOUS_MONTH')}
          disabled={!canGoToPreviousMonth}
          className={cn(
            "rounded-full hover:bg-brand/10 h-9 w-9",
            canGoToPreviousMonth ? "text-foreground/70" : "text-foreground/30 cursor-not-allowed"
          )}
        >
          <ChevronLeft className="h-5 w-5" />
        </Button>

        {/* Single-month mode: show month title in nav bar */}
        {!doubleMonth && (
          <h3 className="text-lg font-display text-brand">
            {format(currentMonth, "MMMM yyyy", { locale })}
          </h3>
        )}
        {/* Double-month: titles are inside each MonthGrid */}
        {doubleMonth && <div />}

        <Button
          onClick={handleNextMonth}
          variant="ghost"
          size="icon"
          aria-label={t('A11Y_NEXT_MONTH')}
          className="rounded-full hover:bg-brand/10 h-9 w-9 text-foreground/70"
        >
          <ChevronRight className="h-5 w-5" />
        </Button>
      </div>

      {doubleMonth ? (
        <div className="flex flex-col sm:flex-row sm:gap-8">
          <div className="flex-1 min-w-0">
            <MonthGrid
              month={currentMonth}
              value={value}
              today={today}
              availabilityMap={availabilityMap}
              hasAvailabilityData={hasAvailabilityData}
              onChange={onChange}
              locale={locale}
            />
          </div>
          <div className="hidden sm:block flex-1 min-w-0">
            <MonthGrid
              month={nextMonth}
              value={value}
              today={today}
              availabilityMap={availabilityMap}
              hasAvailabilityData={hasAvailabilityData}
              onChange={onChange}
              locale={locale}
            />
          </div>
        </div>
      ) : (
        <MonthGrid
          month={currentMonth}
          value={value}
          today={today}
          availabilityMap={availabilityMap}
          hasAvailabilityData={hasAvailabilityData}
          onChange={onChange}
          locale={locale}
          showTitle={false}
        />
      )}

      {isLoading && (
        <div className="absolute inset-0 bg-card/50 flex items-center justify-center rounded-xl">
          <div className="w-6 h-6 border-2 border-brand border-t-transparent rounded-full animate-spin" />
        </div>
      )}
    </div>
  );
};

export default Calendar;
